import { Box, IconButton, SvgIcon, Typography } from '@mui/material';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  Button,
  ConfirmationMessageModal,
  FullScreenLoader,
} from '@Components/index';
import MainSectionLayout from '@Components/MainSectionLayout';
import commonConstants from '@Constants/common.constants';
import Icons from '@Constants/Icons.constants';
import { StyledStack } from '@Containers/UserCallerIds/UserCallerIds.styles';
import BuyVonageCallerIdModal from '@Containers/UserCallerIds/VonageCallerIds/BuyVonageCallerIdModal';
import { BuyVonageCallerIdsProps } from '@Containers/UserCallerIds/VonageCallerIds/BuyVonageCallerIdModal/BuyVonageCallerIdModal.types';
import {
  DeleteCallerIdRequest,
  ProviderCallerIdData,
} from '@Models/callerIds.models';
import { ScriptsQuery } from '@Models/scripts.models';
import {
  useDeleteCallerByIdMutation,
  useGetVerifiedCallersListQuery,
} from '@Services/userCallerIds.service';
import { BaseAlertProps } from '@Typings/global.types';
import {
  formatToUSNumber,
  getDateMMDDYYYYHHMM,
  getRTKQuery,
} from '@Utils/common/common.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

const VonageCallerIds: FunctionComponent<BuyVonageCallerIdsProps> = (
  props: BuyVonageCallerIdsProps
): JSX.Element => {
  const { isAdmin } = props;

  const { t } = useTranslation();
  const { triggerAlert } = useAlert();

  /** STATES */
  const [showBuyVonageCallerIdModal, setShowBuyVonageCallerIdModal] =
    useState(false);

  const [deleteConfirmationMessage, setDeleteConfirmationMessage] =
    useState<React.ReactNode | null>(null);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false);
  const [deleteCallerIdData, setDeleteCallerIdData] =
    useState<DeleteCallerIdRequest>({
      id: '',
      userId: '',
    });

  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });

  const location = useLocation();
  const callerIdQuery: ScriptsQuery = useMemo(
    () => getRTKQuery(location.search),
    [location.search]
  );

  /** RTK QUERY HOOK */
  // load a list of Vonage caller Ids
  const {
    data: dataOfCallerList = [],
    isLoading: loadingCallerList,
    isFetching: fetchingCallerList,
    isError: isErrorForGetCallerList,
    error: errorForGetCallerList,
  } = useGetVerifiedCallersListQuery({
    userId: callerIdQuery.userId?.toString() || '',
  });

  // delete callerId
  const [deleteCallerId, deleteCallerIdMethods] = useDeleteCallerByIdMutation();

  /** Table Loading State */
  const loading = loadingCallerList || fetchingCallerList;

  /** Page Heading */
  const headingText = t('userCallerIds.User Caller IDs');

  /**
   * Deletes callerId
   * @param id Id of the caller
   * @param phoneNumber phone number of the callerId
   */
  const handleDeleteCallerId = (id: string, phoneNumber: string) => {
    const userId: string = callerIdQuery.userId
      ? callerIdQuery.userId?.toString()
      : '';

    setDeleteCallerIdData({ id, userId });
    const phone = formatToUSNumber(phoneNumber);

    const message: React.ReactNode = (
      <Typography
        variant="main"
        sx={{
          '& .phone': {
            color: 'secondary.main',
          },
        }}
      >
        <Trans
          i18nKey="common.confirmationMessages.confirmDelete"
          values={{ phone }}
          components={{
            strong: <strong />,
            span: <span className="phone" />,
          }}
        />
      </Typography>
    );
    setDeleteConfirmationMessage(message);
    setIsDeleteConfirmationModalOpen(true);
  };

  /**
   * Hides the confirmation modal and deletes the callerId
   * @param data DeleteCallerIdRequest object
   * @returns delete mutation
   */
  const deleteCallerIdActionHandler = (data: DeleteCallerIdRequest) => {
    setDeleteCallerIdData({ id: '', userId: '' });
    setIsDeleteConfirmationModalOpen(false);
    if (data.id) {
      return deleteCallerId(data);
    }
  };

  /**
   * Error and Success alerts for Delete CallerId
   */
  useEffect(() => {
    if (deleteCallerIdMethods.isError && deleteCallerIdMethods.error) {
      triggerAlert(deleteCallerIdMethods.error, setAlertState);
    }
    if (deleteCallerIdMethods.isSuccess) {
      triggerAlert(
        commonConstants.GLOBAL_MESSAGES.COMMON_CALLER_ID_DELETE,
        setAlertState
      );
    }
  }, [
    deleteCallerIdMethods.isError,
    deleteCallerIdMethods.isSuccess,
    deleteCallerIdMethods.error,
    deleteCallerIdMethods.data,
    triggerAlert,
  ]);

  /**
   * Shows and hides modal for buying Vonage phone number confirmation
   */
  const addNewNumber = () => {
    setShowBuyVonageCallerIdModal(true);
  };

  /**
   * Error Alerts for the Get Caller Ids Query
   */
  useEffect(() => {
    if (isErrorForGetCallerList && errorForGetCallerList) {
      triggerAlert(errorForGetCallerList, setAlertState);
    }
  }, [triggerAlert, errorForGetCallerList, isErrorForGetCallerList]);

  /**
   * Constructs the UI (row) of the caller table
   * @param id unique id of the caller
   * @param phone phone number of the caller
   * @param date createdAt date
   * @param handleDeletingCallerId callback method to delete the caller id
   * @returns a single row, populating the data
   */
  const CallerTableRow = (
    id: string,
    phone: string,
    date: string,
    handleDeletingCallerId: (id: string, phone: string) => void
  ): JSX.Element => (
    <StyledStack key={id}>
      <Typography className="phone-number" variant="main">
        {formatToUSNumber(phone)}
      </Typography>
      <Typography className="date" variant="main">
        {t('userCallerIds.Created Date', {
          interpolation: {
            escapeValue: false,
          },
          date: getDateMMDDYYYYHHMM(new Date(date), true),
        })}
      </Typography>
      {isAdmin && (
        <IconButton
          onClick={() => handleDeletingCallerId(id, phone)}
          className="delete-icon"
          aria-label="delete"
        >
          <SvgIcon>{Icons.delete}</SvgIcon>
        </IconButton>
      )}
    </StyledStack>
  );

  /**
   * Conditionally shows/hides button for opening phone number confirmation modal
   */
  const AddNewCallerIdButton = (
    <>
      {dataOfCallerList.length < 1 && (
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <Button onClick={() => addNewNumber()} icon="add">
            {t('common.messages.Add Number')}
          </Button>
        </Box>
      )}
    </>
  );

  /**
   * Table component to render Phone, Created At date and Delete
   */
  const TableComponent = (
    <>
      {dataOfCallerList.length > 0 &&
        dataOfCallerList.map(
          (item: ProviderCallerIdData): JSX.Element =>
            CallerTableRow(
              item.id,
              item.callerId,
              item.createdAt,
              handleDeleteCallerId
            )
        )}
      <BuyVonageCallerIdModal
        userId={callerIdQuery.userId}
        isOpen={showBuyVonageCallerIdModal}
        setIsOpen={setShowBuyVonageCallerIdModal}
        hasUserTpr={
          callerIdQuery.hasUserTpr
            ? callerIdQuery.hasUserTpr?.toString() === 'true'
            : false
        }
      />
      <ConfirmationMessageModal
        isModalOpen={isDeleteConfirmationModalOpen}
        setIsModalOpen={setIsDeleteConfirmationModalOpen}
        message={deleteConfirmationMessage}
        onClick={() => deleteCallerIdActionHandler(deleteCallerIdData)}
      />
      {dataOfCallerList.length === 0 && (
        <Typography
          variant="main"
          fontWeight="bold"
          textAlign="center"
          sx={{ my: 3.2, color: 'action.disabled' }}
        >
          {t('common.table.No data to show')}
        </Typography>
      )}
    </>
  );

  return (
    <>
      {loading ? (
        <FullScreenLoader data-testid="loader" />
      ) : (
        <>
          <MainSectionLayout
            alertState={alertState}
            heading={headingText}
            filters={AddNewCallerIdButton}
            table={TableComponent}
          />
        </>
      )}
    </>
  );
};

export default VonageCallerIds;
